'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { getChainAddresses } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { isAddressEqual } from 'viem';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import VaultV1TransactionInfoCard from '@/components/common/PositionManager/VaultV1TransactionInfoCard';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { WrapSegment } from '@/components/common/WrapSegment';
import { useVaultV1DepositLogic } from '@/components/VaultPage/VaultV1/hooks/useVaultV1DepositLogic';
import { useVaultV1ActionPanelData } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/useVaultV1ActionPanelData';
import VaultV1OperationShell, { VaultV1FieldError } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/VaultV1OperationPanel/VaultV1OperationShell';
import { VaultV1DepositReviewDialog } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/VaultV1OperationPanel/VaultV1ReviewDialog';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import PositionManagerDepositDialogLoading from './PositionManagerDepositDialogLoading';
interface PositionManagerDepositDialogContentProps {
  vaultAddress: Address;
  chainId: ChainId;
  /** The vault's asset, when the caller knows it before the vault data loads. */
  assetAddress?: Address;
}
const PositionManagerDepositDialogContent = t0 => {
  const $ = _c(75);
  const {
    vaultAddress,
    chainId,
    assetAddress
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    account
  } = useWeb3Context();
  const {
    vaultV2WrapDeposit: isWrapDepositEnabled
  } = useFeatureFlags();
  let t1;
  if ($[0] !== account || $[1] !== assetAddress || $[2] !== chainId || $[3] !== isWrapDepositEnabled) {
    const {
      wNative
    } = getChainAddresses(chainId);
    t1 = isWrapDepositEnabled && account.isConnected && !!assetAddress && !!wNative && isAddressEqual(assetAddress, wNative);
    $[0] = account;
    $[1] = assetAddress;
    $[2] = chainId;
    $[3] = isWrapDepositEnabled;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const reservesWrapRow = t1;
  let t2;
  if ($[5] !== chainId || $[6] !== vaultAddress) {
    t2 = {
      vaultAddress,
      chainId
    };
    $[5] = chainId;
    $[6] = vaultAddress;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    vaultData,
    isLoading: isQueryLoading,
    error: queryError
  } = useVaultV1ActionPanelData(t2);
  const t3 = vaultData?.asset;
  let t4;
  if ($[8] !== t3) {
    t4 = getParsedValue(t3, parseApiAsset);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const vaultAsset = t4;
  let t5;
  if ($[10] !== vaultAddress || $[11] !== vaultData) {
    t5 = {
      vaultData,
      vaultAddress
    };
    $[10] = vaultAddress;
    $[11] = vaultData;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxDeposit,
    userVaultAssets,
    finalPosition,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    error,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    refetchPosition,
    resetInput,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    erc20Amount,
    nativeAmount,
    wrappableNativeBalance,
    isNativeBalancePending
  } = useVaultV1DepositLogic(t5);
  let t6;
  if ($[13] !== refetchPosition || $[14] !== resetInput) {
    t6 = () => {
      resetInput();
      refetchPosition();
    };
    $[13] = refetchPosition;
    $[14] = resetInput;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onTxSuccess = t6;
  let t7;
  if ($[16] !== erc20Amount || $[17] !== finalPosition || $[18] !== inputAmount || $[19] !== nativeAmount || $[20] !== onTxSuccess || $[21] !== toggleDialog || $[22] !== userVaultAssets || $[23] !== vaultAddress || $[24] !== vaultAsset) {
    t7 = () => {
      if (!isDefined(userVaultAssets) || !vaultAsset) {
        throw new Error("Missing data to open review dialog");
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV1DepositReviewDialog vaultAddress={vaultAddress} inputAmount={inputAmount} erc20Amount={erc20Amount} nativeAmount={nativeAmount} vaultAsset={vaultAsset} userPosition={userVaultAssets} finalPosition={finalPosition} onTxSuccess={onTxSuccess} entryPoint="position_manager" /></SectionErrorBoundary>, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[16] = erc20Amount;
    $[17] = finalPosition;
    $[18] = inputAmount;
    $[19] = nativeAmount;
    $[20] = onTxSuccess;
    $[21] = toggleDialog;
    $[22] = userVaultAssets;
    $[23] = vaultAddress;
    $[24] = vaultAsset;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const openReviewDialog = t7;
  if (isQueryLoading) {
    let t8;
    if ($[26] !== reservesWrapRow) {
      t8 = <PositionManagerDepositDialogLoading reservesWrapRow={reservesWrapRow} />;
      $[26] = reservesWrapRow;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  if (queryError || !vaultData || !vaultAsset) {
    let t8;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ErrorServiceUnavailable minHeight="400px" />;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  const t8 = `Deposit ${vaultAsset.symbol}`;
  const t9 = maxDeposit?.value;
  let t10;
  if ($[29] !== inputAmount || $[30] !== isMax || $[31] !== isMaxLoading || $[32] !== onMaxClick || $[33] !== setInputAmount || $[34] !== t8 || $[35] !== t9 || $[36] !== vaultAsset) {
    t10 = <CompactAssetInput header={t8} amount={inputAmount} token={vaultAsset} onChange={setInputAmount} maxValue={t9} onMaxClick={onMaxClick} isMax={isMax} loading={isMaxLoading} />;
    $[29] = inputAmount;
    $[30] = isMax;
    $[31] = isMaxLoading;
    $[32] = onMaxClick;
    $[33] = setInputAmount;
    $[34] = t8;
    $[35] = t9;
    $[36] = vaultAsset;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== error.userBalanceError) {
    t11 = <VaultV1FieldError error={error.userBalanceError} message="Error fetching wallet balance." />;
    $[38] = error.userBalanceError;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== error.nativeBalanceError) {
    t12 = <VaultV1FieldError error={error.nativeBalanceError} message="Error fetching native balance." />;
    $[40] = error.nativeBalanceError;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== error.userVaultPositionError) {
    t13 = <VaultV1FieldError error={error.userVaultPositionError} message="Error fetching vault position." />;
    $[42] = error.userVaultPositionError;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t11 || $[45] !== t12 || $[46] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const t15 = vaultAsset?.symbol;
  let t16;
  if ($[48] !== isNativeBalancePending || $[49] !== isWNativeVault || $[50] !== isWrapping || $[51] !== isWrappingToggleLoading || $[52] !== nativeSymbol || $[53] !== onWrappingChange || $[54] !== t15 || $[55] !== wrappableNativeBalance) {
    t16 = <WrapSegment id="position-manager-deposit" enabled={true} isWNative={isWNativeVault} wrappableNativeBalance={wrappableNativeBalance} isNativeBalancePending={isNativeBalancePending} wrappedSymbol={t15} nativeSymbol={nativeSymbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[48] = isNativeBalancePending;
    $[49] = isWNativeVault;
    $[50] = isWrapping;
    $[51] = isWrappingToggleLoading;
    $[52] = nativeSymbol;
    $[53] = onWrappingChange;
    $[54] = t15;
    $[55] = wrappableNativeBalance;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== finalPosition || $[58] !== userVaultAssets || $[59] !== vaultAddress || $[60] !== vaultAsset || $[61] !== vaultData) {
    t17 = <VaultV1TransactionInfoCard vaultData={vaultData} vaultAddress={vaultAddress} vaultAsset={vaultAsset} initialPosition={userVaultAssets} finalPosition={finalPosition} />;
    $[57] = finalPosition;
    $[58] = userVaultAssets;
    $[59] = vaultAddress;
    $[60] = vaultAsset;
    $[61] = vaultData;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const t18 = !bundleCanTransfer;
  let t19;
  if ($[63] !== canTransfer || $[64] !== disabledLabel || $[65] !== isLoading || $[66] !== openReviewDialog || $[67] !== t10 || $[68] !== t14 || $[69] !== t16 || $[70] !== t17 || $[71] !== t18 || $[72] !== tooltipMessage || $[73] !== vaultAsset.address) {
    t19 = <VaultV1OperationShell assetInput={t10} fieldErrors={t14} wrappingControl={t16} summary={t17} assetAddress={vaultAsset.address} canTransfer={canTransfer} isBundleNotAuthorized={t18} handleFinalize={openReviewDialog} isLoading={isLoading} reviewLabel="Review Deposit" disabledLabel={disabledLabel} tooltipMessage={tooltipMessage} />;
    $[63] = canTransfer;
    $[64] = disabledLabel;
    $[65] = isLoading;
    $[66] = openReviewDialog;
    $[67] = t10;
    $[68] = t14;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
    $[72] = tooltipMessage;
    $[73] = vaultAsset.address;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  return t19;
};
export default PositionManagerDepositDialogContent;